import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import {
  CmsService,
  Country,
  GlobalMessageService,
  Region,
  RoutingService,
  TranslationService,
  User
} from '@spartacus/core';
import { AddressBookComponentService, AddressFormComponent } from '@spartacus/user/profile/components';
import { LaunchDialogService } from '@spartacus/storefront';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { UserProfileFacade } from "@spartacus/user/profile/root";
import { Observable, Subscription } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import {
  CustomCheckoutDeliveryModesService
} from 'src/app/spartacus/custom/core/src/checkout/facade/custom-checkout-delivery-modes.service';
import { ICON_TYPE } from "../../../../../../../custom/icons/icon.model";
import { CustomUserAddressService } from '../../../../../core/user/facade/profile/custom-user-address.service';
import { CustomValidators } from '../../../../../shared/utils/validators/custom-validators';
import { CustomCheckoutService } from "src/app/spartacus/custom/core/src/checkout/facade/custom-checkout.service";

@Component({
  selector: 'cx-address-form',
  templateUrl: './custom-address-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomAddressFormComponent extends AddressFormComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  @Input() reset: boolean;
  regions$: Observable<Region[]> = this.getRegions();

  user: User;
  user$: Observable<User | undefined> = this.userAccount.get();
  addressForm: UntypedFormGroup;
  guestEmail: string;
  iconTypes = ICON_TYPE;

  constructor(
    protected fb: UntypedFormBuilder,
    protected userAddressService: CustomUserAddressService,
    protected globalMessageService: GlobalMessageService,
    protected translation: TranslationService,
    protected launchDialogService: LaunchDialogService,
    protected userProfileFacade: UserProfileFacade,
    protected userAccount: UserAccountFacade,
    protected activeCartService: ActiveCartFacade,
    protected service: AddressBookComponentService,
    protected routingService: RoutingService,
    protected cmsService: CmsService,
    protected customCheckoutDeliveryModesService: CustomCheckoutDeliveryModesService,
    protected customCheckoutService: CustomCheckoutService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    super(fb, userAddressService, globalMessageService, translation, launchDialogService, userProfileFacade);
    this.addressForm = this.fb.group({
      country: this.fb.group({
        isocode: ['ES', Validators.required],
      }),
      titleCode: [''],
      firstName: [this.reset ? '' : this.user?.commercialName, [Validators.required, Validators.maxLength(70)]],
      line1: [this.reset ? '' : this.user?.defaultBillingAddress?.line1, [Validators.required, Validators.maxLength(70)]],
      line2: [this.reset ? '' : this.user?.defaultBillingAddress?.line2],
      town: [this.reset ? '' : this.user?.defaultBillingAddress?.town, [Validators.required, Validators.maxLength(35)]],
      postalCode: [this.reset ? '' : this.user?.defaultBillingAddress?.postalCode, [
        Validators.required,
        Validators.maxLength(5),
        Validators.minLength(5),
        CustomValidators.onlyNumbers,
      ]],
      region: this.fb.group({
        isocode: [this.reset ? '' : this.user?.defaultBillingAddress?.region?.isocode ? this.user?.defaultBillingAddress?.region?.isocode : '', [Validators.required, CustomValidators.regionCodeStartsWith('postalCode')]],
      }),
      phone: [this.reset ? '' : this.user?.defaultBillingAddress?.phone, [Validators.required, CustomValidators.phoneValidator]],
      email: [this.user?.displayUid, [Validators.required, CustomValidators.emailValidator]],
      defaultAddress: [false],
    });
    this.validatePostalCode();
  }

  ngOnInit(): void {
    this.countries$ = this.userAddressService.getDeliveryCountries().pipe(
      tap((countries: Country[]) => {
        if (Object.keys(countries).length === 0) {
          this.userAddressService.loadDeliveryCountries();
        }
      })
    );
    this.subscriptions.add(this.user$.subscribe(user => this.user = user));
    if (this.addressData && Object.keys(this.addressData).length !== 0) {
      this.addressForm.patchValue(this.addressData);
      this.countrySelected(this.addressData.country);
      if (this.addressData.region) {
        this.regionSelected(this.addressData.region);
      }
    }
    this.subscriptions.add(this.customCheckoutDeliveryModesService
      .getLoadSupportedDeliveryModeProcess().subscribe(success => {
        if (success) {
          this.addresses$ = this.userAddressService.getAddresses();
        }
      }));
    this.subscriptions.add(this.userAddressService.getAddressesLoading().subscribe(address => {
      if (!address) {
        this.subscriptions.add(this.activeCartService.getActive().subscribe(response => {
          // DRF, guestUser needs the data from customer and loggedIn from the user
          if (response?.customer) {
            this.addressForm.controls.email.setValue(response?.customer?.contactEmail);
          } else {
            this.addressForm.controls.email.setValue(this.user?.contactEmail);
          }
        }));
      }
    }));

    this.subscriptions.add(
      this.regions$.pipe(
        filter(regions => regions.length > 0),
        first()
      ).subscribe(regions => {
        const isExistRegions = regions.find(region => region.isocode === this.addressData?.region.isocode)
        if (!isExistRegions) {
          const shippingResetValue = {
            titleCode: "",
            firstName: null,
            line1: null,
            line2: null,
            town: null,
            postalCode: null,
            region: {
              isocode: ""
            },
            phone: null,
          };
          this.addressForm.patchValue(shippingResetValue);
        }
      })
    );

    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo(0, 0);
    }
  }

  goToCart(): void {
    this.routingService.go('/cart');
  }

  verifyAddress(): void {
    if (this.addressForm.valid) {
      this.customCheckoutService.addShippingInfoEvent();
      this.submitAddress.emit(this.addressForm.value);
    } else {
      this.addressForm.markAllAsTouched();
    }
  }

  onSubmit(): void {
    this.verifyAddress();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  validatePostalCode(): void {
    this.addressForm.get('region.isocode')?.updateValueAndValidity();
  }

  getRegions(): Observable<Region[]> {
    return this.cmsService.getCurrentPage().pipe(
      map((page) => !!page.label.includes('checkout')),
      switchMap((isCheckoutPage) => {
          if (!!isCheckoutPage) {
            return this.activeCartService.getActive().pipe(
              map((data) => !!data?.deliveryZone?.code ? data?.deliveryZone?.code : 'continentalEurope'),
              switchMap((zone: string) => this.userAddressService.getRegionsByZone('ES', zone)));
          }
          return this.userAddressService.getRegions('ES');
        }
      ));
  }
}
